/**
 * One capability, on or off — ROADMAP 9.12.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * A SWITCH, NOT A CHECKBOX, AND IT SAYS THE WORD
 *
 * `components/ui` has no switch primitive, and a native checkbox was the
 * cheap answer. It is the wrong one on this table: a column of bare
 * checkboxes does not say which state is the dangerous one, and these are
 * order-path capabilities where "on" means the dispatcher may now do
 * something it could not before.
 *
 * So the control carries the word. ON is tinted and legible at a glance down
 * the column; off is a muted outline. The tint is the ACCENT rather than
 * success-green, because none of these is good or bad — they are permissions,
 * and colouring them like a P&L would read as approval.
 *
 * `aria-pressed` rather than a checkbox role: it is a toggle button, and a
 * screen reader should hear the capability's name and its state, not "check
 * box, not checked".
 * ─────────────────────────────────────────────────────────────────────────────
 */

import { Loader2 } from 'lucide-react'
import { cn } from '@/lib/utils'

export function CapabilityToggle({
  on,
  label,
  onChange,
  busy = false,
  disabled = false,
  hint,
  testId,
}: {
  on: boolean
  /** Named for the reader, never shown — the column header says it. */
  label: string
  onChange: (next: boolean) => void
  busy?: boolean
  disabled?: boolean
  /** Why it cannot be changed, or what it will do. The native tooltip. */
  hint?: string
  testId?: string
}) {
  return (
    <button
      type="button"
      aria-pressed={on}
      aria-label={`${label}: ${on ? 'on' : 'off'}`}
      title={hint}
      disabled={disabled || busy}
      data-testid={testId}
      onClick={() => onChange(!on)}
      className={cn(
        'inline-flex h-6 w-[52px] shrink-0 items-center justify-center gap-1 rounded-full border',
        'text-[9.5px] font-bold uppercase tracking-wider transition-colors',
        'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary',
        'disabled:cursor-not-allowed disabled:opacity-45',
        on
          ? 'border-primary/45 bg-primary/12 text-primary'
          : 'border-border bg-transparent text-muted-foreground hover:border-muted-foreground/50',
      )}
    >
      {busy ? <Loader2 size={11} className="animate-spin" /> : on ? 'ON' : 'OFF'}
    </button>
  )
}
